// ==========================================================================
// Navigation Intersection Observer
//
// Purpose: Automatically highlight the active navigation item based on scroll position.
// Prevents "nav dancing" during smooth scrolling by using a simple boolean flag.
//
// Key Concepts:
// - IntersectionObserver API: Detects when sections enter the viewport
// - rootMargin: Creates an "active zone" in the middle of the viewport
// - Boolean flag gating: Prevents conflicts during user-initiated navigation
// - CSS scroll-behavior: Handles smooth scrolling animations
//
// Architecture:
// 1. Observer watches sections for intersection events
// 2. Updates nav state when sections enter the "active zone"
// 3. User clicks set a flag to temporarily ignore observer events
// 4. Flag resets after 800ms (covers all transition durations)
//
// Why This Approach Works:
// - Simple and reliable (no complex Promise.race() with animation events)
// - No memory leaks (no event listener management)
// - Browser-agnostic (works across all modern browsers)
// - Easy to debug and maintain
//
// ==========================================================================
// DEBUG: Visual overlay for IntersectionObserver root margin
//
// This helper draws colored bands over the viewport to visualize exactly
// where the IntersectionObserver "active zone" is based on the current
// `rootMargin` configuration.
//
// Current Configuration:
// - rootMargin: '-30% 0px -30% 0px' (top, right, bottom, left)
// - Active zone: Middle ~40% of viewport
// - Ignored zones: Top 30% and bottom 30%
//
// Usage (dev only):
//   window.__showNavIoDebug && window.__showNavIoDebug();
//
// Visual Guide:
// - Red bands = Ignored areas (top/bottom 30%)
// - Green band = Active zone where sections become "current"
function $caa975bf6fc5e650$var$createDebugOverlay() {
    // NOTE: Keep these percentages in sync with the observer options below!
    const topIgnored = 30; // Must match rootMargin top value
    const bottomIgnored = 30; // Must match rootMargin bottom value
    const overlay = document.createElement('div');
    overlay.id = 'io-debug-overlay';
    overlay.innerHTML = `
    <div style="
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      height: 33vh;
      background: rgba(255, 0, 0, 0.15);
      pointer-events: none;
      z-index: 9999;
      border-bottom: 2px dashed red;
    ">
      <span style="position: absolute; bottom: 4px; left: 8px; color: red; font-size: var(--font-size-xs); font-weight: bold;">
        \u{2191} IGNORED (top 30%)
      </span>
    </div>
    <div style="
      position: fixed;
      bottom: 0;
      left: 0;
      right: 0;
      height: 33vh;
      background: rgba(255, 0, 0, 0.15);
      pointer-events: none;
      z-index: 9999;
      border-top: 2px dashed red;
    ">
      <span style="position: absolute; top: 4px; left: 8px; color: red; font-size: var(--font-size-xs); font-weight: bold;">
        \u{2193} IGNORED (bottom 30%)
      </span>
    </div>
    <div style="
      position: fixed;
      top: 30vh;
      bottom: 30vh;
      left: 0;
      right: 0;
      background: rgba(0, 255, 0, 0.1);
      pointer-events: none;
      z-index: 9998;
      border: 2px solid green;
    ">
      <span style="position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%); color: green; font-size: var(--font-size-sm); font-weight: bold;">
        ACTIVE ZONE (middle ~40% of viewport)
      </span>
    </div>
  `;
    document.body.appendChild(overlay);
}
function $caa975bf6fc5e650$export$7578aff938ece992() {
    // DEBUG hook: from the browser console you can call
    //   window.__showNavIoDebug && window.__showNavIoDebug();
    // to draw the red/green overlay that visualizes the current
    // IntersectionObserver root margin (see `createDebugOverlay`).
    // Only expose in development environments to keep production clean.
    // eslint-disable-next-line no-underscore-dangle
    if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') window.__showNavIoDebug = $caa975bf6fc5e650$var$createDebugOverlay;
    // Sections to observe for navigation updates
    const sectionIds = [
        'home',
        'locums',
        'about',
        'contact',
        'connect'
    ];
    const sections = sectionIds.map((id)=>document.getElementById(id)).filter((el)=>el !== null);
    if (sections.length === 0) return;
    // Navigation link collections for desktop and mobile
    const desktopLinks = Array.from(document.querySelectorAll('.nav-menu .nav-link'));
    const mobileLinks = Array.from(document.querySelectorAll('.mobile-nav__link'));
    if (desktopLinks.length === 0 && mobileLinks.length === 0) return;
    // Utility: Extract section ID from href attribute
    // Example: href="#home" → "home"
    const idFromHref = (href)=>{
        if (!href) return null;
        try {
            const hashIndex = href.indexOf('#');
            if (hashIndex === -1) return null;
            return href.slice(hashIndex + 1);
        } catch  {
            return null;
        }
    };
    const desktopById = new Map();
    const mobileById = new Map();
    desktopLinks.forEach((link)=>{
        const id = idFromHref(link.getAttribute('href'));
        if (id && sectionIds.includes(id)) desktopById.set(id, link);
    });
    mobileLinks.forEach((link)=>{
        const id = idFromHref(link.getAttribute('href'));
        if (id && sectionIds.includes(id)) mobileById.set(id, link);
    });
    // Navigation state management
    let currentSectionId = null;
    // Core function: Update navigation active states
    // Updates both desktop (CSS classes) and mobile (ARIA attributes)
    const updateNavForSection = (sectionId)=>{
        if (!sectionId || sectionId === currentSectionId) return;
        // Debug logging (remove in production)
        console.log(`\u{1F9ED} Nav update: ${currentSectionId} \u{2192} ${sectionId}`);
        currentSectionId = sectionId;
        // Desktop navigation: Use CSS classes
        desktopById.forEach((link, id)=>{
            if (id === sectionId) link.classList.add('nav-link--active');
            else link.classList.remove('nav-link--active');
        });
        // Mobile navigation: Use ARIA attributes
        mobileById.forEach((link, id)=>{
            if (id === sectionId) link.setAttribute('aria-current', 'page');
            else link.removeAttribute('aria-current');
        });
    };
    // Anti-dancing mechanism: Simple boolean flag approach
    // 
    // PROBLEM: During smooth scrolling, IntersectionObserver fires rapidly as sections
    // enter/exit the viewport. Without protection, the nav highlights "dance" between
    // old and new sections during the scroll animation, creating a jarring user experience.
    //
    // SOLUTION: Use a simple boolean flag to temporarily ignore IntersectionObserver
    // events during user-initiated navigation. This is more reliable than complex
    // Promise.race() with animation events, which have browser compatibility issues.
    let userClickedNavItem = false;
    // Handle navigation clicks with anti-dancing protection
    const handleNavClick = (event)=>{
        const link = event.currentTarget;
        const id = idFromHref(link.getAttribute('href'));
        if (!id || !sectionIds.includes(id)) return;
        // Set flag to temporarily ignore IntersectionObserver events
        // This prevents the nav highlights from dancing during smooth scroll effects
        userClickedNavItem = true;
        // Reset flag after 800ms - carefully chosen timing:
        // - Smooth scroll animation: ~500-600ms (depends on scroll distance)
        // - CSS transitions: 200ms (nav-link transitions)
        // - Total with buffer: 800ms ensures observer stays quiet until scroll completes
        setTimeout(()=>{
            userClickedNavItem = false;
        }, 800);
        // Update navigation state immediately for instant visual feedback
        // User sees the correct highlight right away, then scroll animates smoothly
        updateNavForSection(id);
    };
    [
        ...desktopById.values(),
        ...mobileById.values()
    ].forEach((link)=>{
        link.addEventListener('click', handleNavClick);
    });
    // Choose thresholds based on viewport height to better support small mobiles
    const getThresholdsForViewport = ()=>{
        const h = window.innerHeight || document.documentElement.clientHeight || 0;
        // Very small heights (e.g., landscape phones)
        if (h <= 400) return [
            0.05,
            0.15,
            0.25
        ];
        // Small mobiles (e.g., iPhone SE portrait and similar)
        if (h <= 667) return [
            0.1,
            0.3,
            0.5,
            0.7,
            0.9
        ];
        // Default for larger mobile/tablet/desktop
        return [
            0.2,
            0.4,
            0.6,
            0.8
        ];
    };
    // IntersectionObserver configuration
    // Creates an "active zone" in the middle 40% of the viewport
    // Ignores top 30% and bottom 30% to prevent rapid switching
    if ('IntersectionObserver' in window) {
        const observer = new IntersectionObserver((entries)=>{
            // Skip updates during user-initiated navigation (anti-dancing)
            if (userClickedNavItem) return;
            entries.forEach((entry)=>{
                if (entry.isIntersecting) updateNavForSection(entry.target.id);
            });
        }, {
            // Focus on middle portion of viewport for stable navigation
            // Top 30% and bottom 30% are ignored, middle ~40% is the active zone
            rootMargin: '-30% 0px -30% 0px',
            // Multiple thresholds tuned per viewport height for robust detection
            // Helps small mobile viewports correctly detect the top section
            threshold: getThresholdsForViewport()
        });
        // Start observing all sections
        sections.forEach((section)=>observer.observe(section));
    } else {
        // Fallback for very old browsers without IntersectionObserver support
        // Default to the first available section or 'home'
        const fallbackId = sectionIds.find((id)=>document.getElementById(id)) || 'home';
        updateNavForSection(fallbackId);
    }
}


function $67c73874256ba79d$export$ac308a179e459484() {
    const cards = Array.from(document.querySelectorAll('.staff-card, .services-grid .card--services'));
    if (cards.length === 0) return;
    // Respect reduced motion preferences
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (!('IntersectionObserver' in window) || prefersReducedMotion) return;
    // Add base animation class so CSS can target them
    cards.forEach((card, index)=>{
        card.classList.add('staff-card--scroll');
        card.dataset.scrollIndex = String(index);
    });
    const observer = new IntersectionObserver((entries, obs)=>{
        entries.forEach((entry)=>{
            const card = entry.target;
            const index = Number(card.dataset.scrollIndex || '0');
            // Calculate staggered animation delay for smooth card appearance
            // Formula: index * 220ms = 0ms, 220ms, 440ms, 660ms, 880ms (capped)
            // This creates a deliberate, elegant stagger effect without being too fast or slow
            const delay = Math.min(index * 220, 880);
            if (entry.isIntersecting) window.setTimeout(()=>{
                card.classList.add('staff-card--visible');
            }, delay);
            else // When card leaves the viewport, reset so it can animate again next time
            // Use the same staggered delay for removal
            window.setTimeout(()=>{
                card.classList.remove('staff-card--visible');
            }, delay);
        });
    }, {
        root: null,
        threshold: [
            0.05,
            0.15,
            0.3,
            0.5
        ]
    });
    cards.forEach((card)=>observer.observe(card));
}


function $e1400db3377e1890$export$b1da90ca756b7b6e() {
    const button = document.querySelector('[data-scroll-top]');
    if (!button) console.log('hello i am hot here ');
    button.addEventListener('click', (e)=>{
        e.preventDefault();
        // Accessibility: ensure main-content is focusable and focus it
        const main = document.getElementById('main-content');
        if (main) {
            if (!main.hasAttribute('tabindex')) main.setAttribute('tabindex', '-1');
            main.focus({
                preventScroll: true
            });
        }
        // Navigate to home page with clean URL
        window.history.pushState({}, '', '/');
        // Scroll to top
        document.getElementById("main-content").scrollIntoView({
            behavior: 'smooth',
            block: 'start' // 'start', 'center', 'end', 'nearest'
        });
        // Update navigation active state
        const desktopLinks = document.querySelectorAll('.nav-link');
        desktopLinks.forEach((link)=>{
            if (link.getAttribute('href') === '/') link.classList.add('nav-link--active');
            else link.classList.remove('nav-link--active');
        });
        const mobileLinks = document.querySelectorAll('.mobile-nav__link');
        mobileLinks.forEach((link)=>{
            if (link.getAttribute('href') === '/') link.setAttribute('aria-current', 'page');
            else link.removeAttribute('aria-current');
        });
    });
    const handleScroll = ()=>{
        if (window.scrollY > 300) button.classList.add('scroll-top--visible');
        else button.classList.remove('scroll-top--visible');
    };
    window.addEventListener('scroll', handleScroll);
}


/**
 * Hero Map Replay
 *
 * Replay strategy (why clone/replace?):
 * - The map uses SVG SMIL animations (<animate>, <animateMotion>).
 * - SMIL timelines start when the SVG element is inserted into the DOM.
 * - There is no reliable cross-browser "reset" API for SMIL + CSS animations.
 * - Cloning + replacing the <svg> re-inserts a fresh copy, restarting all timelines.
 * - CSS animations on the map (including the hover-disable timing) also restart.
 */ const $ef70692d977fa26c$export$3bd541efa9ddf5f5 = ()=>{
    const replayButton = document.querySelector('[data-hero-replay]');
    if (!replayButton) return;
    replayButton.addEventListener('click', (e)=>{
        if (replayButton.tagName.toLowerCase() === 'a') e.preventDefault();
        const container = replayButton.closest('.hero-visual');
        if (!container) return;
        const svg = container.querySelector('svg.ca-map');
        if (!svg) return;
        const finalLogo = container.querySelector('.hero-final-logo');
        const clone = svg.cloneNode(true);
        svg.replaceWith(clone);
        if (finalLogo) {
            const finalLogoClone = finalLogo.cloneNode(true);
            finalLogo.replaceWith(finalLogoClone);
        }
    });
};


/**
 * Staff Card Height + Accessibility Helpers
 *
 * What this file does
 * - Measures the content heights of each staff card's front and back faces.
 * - Writes those measurements to CSS custom properties on the card element.
 * - Adds a close button behavior that closes the card without accidentally
 *   re-toggling via the surrounding <label>.
 * - Improves keyboard navigation by removing the back-face close button from
 *   the tab order when the card is closed.
 *
 * Why we need JS for heights
 * - The card uses a 3D flip and the faces are absolutely positioned.
 * - With absolutely positioned faces, pure CSS cannot naturally size the outer
 *   card to the content on the back face.
 * - So we measure real DOM heights and feed them into CSS via variables.
 *
 * Expected markup (per card)
 * - .staff-card (wrapper)
 * - .staff-card__toggle (a checkbox)
 * - .staff-card__inner (a <label> that toggles the checkbox)
 * - .staff-card__face--front (front face)
 * - .staff-card__face--back (back face)
 * - .staff-card__back-content (content wrapper inside back face)
 * - .staff-card__close (button inside back face)
 *
 * CSS variables written by this script (on .staff-card)
 * - --staff-card-front-height: used as the collapsed min-height (front face).
 * - --staff-card-back-height: used as the expanded min-height (back face).
 */ function $02511fd2859c1031$export$fa4f072fc2311a31() {
    const cards = document.querySelectorAll('.staff-card');
    if (cards.length === 0) return;
    /**
   * Small padding allowances added to measured heights so that the content
   * doesn't feel cramped and we avoid 1px clipping due to rounding.
   */ const EXTRA_BOTTOM_PX = 12;
    const EXTRA_FRONT_PX = 8;
    /**
   * Ensure the close button is only reachable by Tab when it is actually usable.
   *
   * Problem:
   * - The close button is inside the (hidden) back face.
   * - Even when the card is closed, that button still exists in the DOM and is
   *   focusable, so keyboard users would hit TWO tab stops per card.
   *
   * Solution:
   * - When the checkbox is unchecked: close button gets tabIndex = -1.
   * - When the checkbox is checked: close button gets tabIndex = 0.
   */ function syncCloseTabIndex(card) {
        const toggle = card.querySelector('.staff-card__toggle, .staff-card-toggle');
        const closeBtn = card.querySelector('.staff-card__close, .staff-card-close');
        if (!(toggle instanceof HTMLInputElement)) return;
        if (!(closeBtn instanceof HTMLButtonElement)) return;
        closeBtn.tabIndex = toggle.checked ? 0 : -1;
    }
    /**
   * Initialize the close button tabIndex sync for all cards.
   *
   * - Runs once on load to set the correct initial tabIndex.
   * - Attaches a change listener to keep it updated as cards open/close.
   */ function initCloseTabIndex() {
        cards.forEach((card)=>{
            const toggle = card.querySelector('.staff-card__toggle, .staff-card-toggle');
            if (!(toggle instanceof HTMLInputElement)) return;
            syncCloseTabIndex(card);
            toggle.addEventListener('change', ()=>syncCloseTabIndex(card));
        });
    }
    /**
   * Close button behavior.
   *
   * Why this needs JS:
   * - The entire card is a <label> that toggles the checkbox.
   * - A click on the close button would normally bubble to the label and
   *   re-toggle the checkbox, causing inconsistent behavior.
   *
   * Accessibility reasons:
   * - The close affordance should be a real <button>, not a decorative span,
   *   so keyboard and screen reader users can discover and activate a clear
   *   "Close details" control.
   * - Without this behavior, the only way to close is an implicit pattern
   *   ("toggle the card again"), which is harder to discover when focus is
   *   deep inside the back-face content.
   * - Preventing the label toggle makes the close button's intent predictable:
   *   it closes (unchecked) rather than behaving like a generic toggle.
   *
   * What we do:
   * - preventDefault + stopPropagation so the label does not toggle.
   * - explicitly uncheck the checkbox.
   * - dispatch a 'change' event so any listeners update.
   */ function initCloseButtons() {
        document.querySelectorAll('.staff-card__close, .staff-card-close').forEach((btn)=>{
            if (!(btn instanceof HTMLButtonElement)) return;
            btn.addEventListener('click', (e)=>{
                // Prevent the surrounding label click from re-toggling the checkbox.
                e.preventDefault();
                e.stopPropagation();
                const card = btn.closest('.staff-card');
                const toggle = card?.querySelector('.staff-card__toggle, .staff-card-toggle');
                if (toggle instanceof HTMLInputElement) {
                    toggle.checked = false;
                    toggle.dispatchEvent(new Event('change', {
                        bubbles: true
                    }));
                }
            });
        });
    }
    /**
   * Measure a single card and update its CSS variables.
   *
   * Key trick:
   * - The faces are absolutely positioned and transformed.
   * - For reliable measurements we temporarily set:
   *   - position: static
   *   - visibility: hidden
   *   - transform: none
   *   Then we read offsetHeight, and finally restore the original styles.
   *
   * This keeps layout stable(no visible jump), but lets us get the true
   * content height.
   */ function measureCard(card) {
        const backContent = card.querySelector('.staff-card__back-content, .staff-card-back-content');
        const frontFace = card.querySelector('.staff-card__face--front, .staff-card-face-front');
        const backFace = card.querySelector('.staff-card__face--back, .staff-card-face-back');
        if (frontFace) {
            const originalFacePosition = frontFace.style.position;
            const originalFaceVisibility = frontFace.style.visibility;
            const originalFaceTransform = frontFace.style.transform;
            frontFace.style.position = 'static';
            frontFace.style.visibility = 'hidden';
            frontFace.style.transform = 'none';
            const frontHeight = frontFace.offsetHeight + EXTRA_FRONT_PX;
            frontFace.style.position = originalFacePosition;
            frontFace.style.visibility = originalFaceVisibility;
            frontFace.style.transform = originalFaceTransform;
            card.style.setProperty('--staff-card-front-height', `${frontHeight}px`);
        }
        if (!backContent || !backFace) return;
        // Temporarily make backContent measurable (position: static, visibility: hidden)
        // Store original styles
        const originalFacePosition = backFace.style.position;
        const originalFaceVisibility = backFace.style.visibility;
        const originalFaceTransform = backFace.style.transform;
        // Make it measurable without showing it
        backFace.style.position = 'static';
        backFace.style.visibility = 'hidden';
        backFace.style.transform = 'none';
        // Measure
        const height = backContent.offsetHeight + EXTRA_BOTTOM_PX;
        // Restore
        backFace.style.position = originalFacePosition;
        backFace.style.visibility = originalFaceVisibility;
        backFace.style.transform = originalFaceTransform;
        // Set CSS variable on the card
        card.style.setProperty('--staff-card-back-height', `${height}px`);
    }
    /**
   * Convenience wrapper to measure all cards.
   */ function measureAndSet() {
        cards.forEach((card)=>measureCard(card));
    }
    // Run on load
    measureAndSet();
    initCloseButtons();
    initCloseTabIndex();
    // Re-measure when content/layout changes (more precise than window resize)
    if ('ResizeObserver' in window) {
        /**
     * ResizeObserver lets us re-measure when the content actually changes size
     * (e.g., responsive wrapping, font loading, image load, etc.).
     */ const ro = new ResizeObserver((entries)=>{
            const uniqueCards = new Set();
            entries.forEach((entry)=>{
                const el = entry.target;
                const card = el.closest('.staff-card');
                if (card) uniqueCards.add(card);
            });
            uniqueCards.forEach((card)=>measureCard(card));
        });
        cards.forEach((card)=>{
            const backContent = card.querySelector('.staff-card__back-content, .staff-card-back-content');
            if (backContent) ro.observe(backContent);
            const frontFace = card.querySelector('.staff-card__face--front, .staff-card-face-front');
            if (frontFace) ro.observe(frontFace);
        });
    } else // Fallback for older browsers
    window.addEventListener('resize', measureAndSet);
}


/*
  ELI5: How this theme toggle works

  - CSS is the only thing that actually changes colors.
  - This JS file does NOT turn dark mode on/off. It only:
    1) sets the checkbox state on page load
    2) remembers the checkbox state in localStorage

  localStorage key:
  - 'salud-theme' stores 'dark' or 'light'.
 */ function $4f024ee0b63cbec0$export$f847871b15e871e2() {
    const themeToggleWrapper = document.getElementById('theme-toggle');
    const themeToggleLabel = themeToggleWrapper?.querySelector('label');
    const themeToggleCheckbox = themeToggleWrapper?.querySelector('input[type="checkbox"]');
    if (!themeToggleWrapper || !themeToggleLabel || !themeToggleCheckbox) return;
    const root = document.documentElement;
    const storageKey = 'salud-theme';
    let didWarn = false;
    const syncAria = (isDark)=>{
        const ariaLabel = isDark ? 'Theme: dark' : 'Theme: light';
        themeToggleCheckbox.setAttribute('aria-checked', String(isDark));
        themeToggleCheckbox.setAttribute('aria-label', ariaLabel);
    };
    const warnOnce = (err)=>{
        if (didWarn) return;
        const hostname = window.location?.hostname || '';
        const isDevHost = [
            'localhost',
            '127.0.0.1',
            ''
        ].includes(hostname) || hostname.endsWith('.local');
        if (isDevHost) {
            console.warn('[theme-toggle] localStorage unavailable.', err);
            didWarn = true;
        }
    };
    // Init
    try {
        const stored = localStorage.getItem(storageKey);
        if (stored === 'dark') themeToggleCheckbox.checked = true;
        else if (stored === 'light') themeToggleCheckbox.checked = false;
        else {
            const userThemePreference = !!(window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches);
            themeToggleCheckbox.checked = userThemePreference;
        }
    } catch (err) {
        themeToggleCheckbox.checked = false;
        warnOnce(err);
    }
    root.setAttribute('data-theme', themeToggleCheckbox.checked ? 'dark' : 'light');
    syncAria(themeToggleCheckbox.checked);
    // Persist
    themeToggleCheckbox.addEventListener('change', ()=>{
        try {
            localStorage.setItem(storageKey, themeToggleCheckbox.checked ? 'dark' : 'light');
        } catch (err) {
            warnOnce(err);
        }
        root.setAttribute('data-theme', themeToggleCheckbox.checked ? 'dark' : 'light');
        syncAria(themeToggleCheckbox.checked);
    });
}


function $cd8e2242aac5ca2f$export$c4b2f00439ee46b0() {
    const components = document.querySelectorAll('[data-component="RoleChips"]');
    if (!components.length) return;
    components.forEach((root)=>{
        const targetInputSelector = root.getAttribute('data-target-input');
        const targetMicrocopySelector = root.getAttribute('data-target-microcopy');
        const selectedClass = root.getAttribute('data-selected-class') || 'is-selected';
        const input = targetInputSelector ? document.querySelector(targetInputSelector) : null;
        const microcopy = targetMicrocopySelector ? document.querySelector(targetMicrocopySelector) : null;
        const roleSelect = root.querySelector('select[data-role-select]');
        const chipsContainer = root.querySelector('[data-role-chips]');
        const buttons = chipsContainer ? Array.from(chipsContainer.querySelectorAll('button[data-role]')) : [];
        const availableRoles = new Set([
            ...buttons.map((btn)=>btn.getAttribute('data-role') || ''),
            ...roleSelect instanceof HTMLSelectElement ? Array.from(roleSelect.options).map((opt)=>opt.value || '') : []
        ].filter(Boolean));
        const setSelectedRole = (role)=>{
            buttons.forEach((btn)=>{
                const isSelected = btn.getAttribute('data-role') === role;
                btn.classList.toggle(selectedClass, isSelected);
                btn.setAttribute('aria-pressed', isSelected ? 'true' : 'false');
            });
            if (input) input.value = role;
            if (roleSelect instanceof HTMLSelectElement) {
                if (role && roleSelect.value !== role) roleSelect.value = role;
            }
            if (microcopy && role) {
                const roleDisplayNames = {
                    'Psychiatrist': 'Psychiatrist',
                    'Psychologist': 'Psychologist',
                    'Licensed Clinical Social Worker': 'LCSW',
                    'Licensed Marriage and Family Therapist': 'LMFT',
                    'Primary Care Physician': 'PCP',
                    'Nurse Practitioner': 'NP',
                    'Physician Assistant': 'PA',
                    'CRNA': 'CRNA',
                    'Nurse': 'RN',
                    'Licensed Professional Clinical Counselor': 'LPCC',
                    'Others': 'Others'
                };
                const displayName = roleDisplayNames[role] || role;
                microcopy.innerHTML = `Great \u{2014} we'll prioritize matches for <span class="role-chip">${displayName}</span> Providers that fit your license and location.`;
            }
        };
        const defaultRoleProp = root.getAttribute('data-default-role') || '';
        const firstRole = buttons[0] && (buttons[0].getAttribute('data-role') || '') || (roleSelect instanceof HTMLSelectElement ? roleSelect.value || '' : '') || '';
        let initialRole = input && input.value ? input.value : '';
        if (!initialRole) initialRole = defaultRoleProp || firstRole;
        if (initialRole && !availableRoles.has(initialRole)) initialRole = firstRole;
        if (initialRole) setSelectedRole(initialRole);
        if (roleSelect instanceof HTMLSelectElement) roleSelect.addEventListener('change', ()=>{
            const role = roleSelect.value || '';
            if (!role) return;
            setSelectedRole(role);
        });
        if (chipsContainer) chipsContainer.addEventListener('click', (e)=>{
            const btn = e.target instanceof Element ? e.target.closest('button[data-role]') : null;
            if (!btn) return;
            const role = btn.getAttribute('data-role') || '';
            if (!role) return;
            // single + locked: clicking the selected chip keeps it selected.
            setSelectedRole(role);
        });
    });
}


/**
 * Creates a form validator for the multi-step application form.
 * Uses event delegation to efficiently validate fields only in the active step.
 *
 * @param {HTMLFormElement} form - The form element to validate
 * @returns {Object} Validator object with methods for validation
 */ function $251048abdb9fb148$export$c881797088684461(form) {
    const steps = Array.from(form.querySelectorAll('fieldset[data-step]'));
    let isNavigationHappening = false; // Flag to track Next button clicks
    let currentStepIndex = 0; // Track current step for delegation
    const textLikeTypes = [
        'email',
        'url'
    ]; // Only trim fields that can't have spaces
    const sanitizeFieldValue = (field)=>{
        const isTrimmableInput = field instanceof HTMLInputElement && textLikeTypes.includes(field.type);
        // Don't trim textareas at all - they should allow free-form text including spaces
        if (!isTrimmableInput) return;
        const trimmed = field.value.trim();
        if (trimmed !== field.value) field.value = trimmed;
    };
    const getMaxLength = (field)=>{
        if (!field.hasAttribute('maxlength')) return null;
        const parsed = parseInt(field.getAttribute('maxlength'), 10);
        return Number.isFinite(parsed) ? parsed : null;
    };
    const getErrorElement = (field)=>{
        const errorId = field.getAttribute('aria-describedby');
        if (!errorId) return null;
        try {
            return form.querySelector(`#${CSS.escape(errorId)}`);
        } catch  {
            return document.getElementById(errorId);
        }
    };
    const clearFieldError = (field)=>{
        const errorEl = getErrorElement(field);
        if (errorEl) errorEl.textContent = '';
        field.removeAttribute('aria-invalid');
    };
    const showFieldError = (field, message)=>{
        const errorEl = getErrorElement(field);
        if (errorEl) {
            // Use custom messages based on field name/id
            const customMessage = getCustomErrorMessage(field);
            errorEl.textContent = customMessage || message;
        }
        field.setAttribute('aria-invalid', 'true');
    };
    const getCustomErrorMessage = (field)=>{
        const fieldName = field.name || field.id || '';
        if (fieldName === 'phone' && field instanceof HTMLInputElement && field.type === 'tel' && typeof field.value === 'string' && field.value.trim() !== '') {
            const phoneRegex = /^[\d\s\-\+\(\)]+$/;
            const digitsCount = field.value.replace(/\D/g, '').length;
            if (phoneRegex.test(field.value) && digitsCount >= 10) return null;
        }
        // Custom messages for specific fields
        const messages = {
            'name': 'Please enter your full name',
            'email': 'Please enter a valid email address',
            'phone': 'Please enter your phone number',
            'licenseType': 'Please select your profession',
            'licenseState': 'Please choose your licensed state'
        };
        return messages[fieldName] || null;
    };
    const shouldValidateField = (field)=>{
        // Don't validate disabled fields
        if (field.disabled) return false;
        // Always validate required fields
        if (field.hasAttribute('required')) return true;
        // For hidden fields, only validate if they have a value (like role selection)
        if (field.type === 'hidden') return Boolean(field.value);
        const value = typeof field.value === 'string' ? field.value.trim() : field.value;
        if (field instanceof HTMLSelectElement) {
            const firstOptionValue = field.options.length ? field.options[0].value : '';
            return value !== '' && value !== firstOptionValue;
        }
        if (field instanceof HTMLInputElement && (field.type === 'checkbox' || field.type === 'radio')) return field.checked;
        return Boolean(value);
    };
    const validateField = (field)=>{
        sanitizeFieldValue(field);
        const maxLen = getMaxLength(field);
        // Custom email validation
        if (field.type === 'email' && field.value) {
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            if (!emailRegex.test(field.value)) {
                showFieldError(field, 'Please enter a valid email address (example@domain.com)');
                return false;
            }
            // Skip browser validation for email fields - we validated above
            clearFieldError(field);
            return true;
        }
        // Custom phone validation for tel fields
        if (field.type === 'tel' && field.value) {
            const phoneRegex = /^[\d\s\-\+\(\)]+$/;
            if (!phoneRegex.test(field.value)) {
                showFieldError(field, 'Phone number can only contain digits, spaces, dashes, plus, and parentheses');
                return false;
            }
            if (field.value.replace(/\D/g, '').length < 10) {
                showFieldError(field, 'Phone number must have at least 10 digits');
                return false;
            }
            // Skip browser validation for phone fields - we validated above
            clearFieldError(field);
            return true;
        }
        // For optional fields with maxlength:
        // - Empty is fine
        // - At/over the limit: show a message (warning/error)
        const isOptional = !field.hasAttribute('required');
        if (isOptional && maxLen !== null) {
            const len = field.value.length;
            if (len > maxLen) {
                showFieldError(field, `Please enter no more than ${maxLen} characters.`);
                return false;
            }
            if (len >= maxLen && len !== 0) {
                const errorEl = getErrorElement(field);
                if (errorEl) errorEl.textContent = `You've reached the ${maxLen}-character limit.`;
                field.removeAttribute('aria-invalid');
                return true;
            }
            clearFieldError(field);
            return true;
        }
        if (!shouldValidateField(field)) {
            clearFieldError(field);
            return true;
        }
        const isValid = field.checkValidity();
        if (isValid) {
            clearFieldError(field);
            return true;
        }
        const message = field.validationMessage || 'Please complete this field.';
        showFieldError(field, message);
        return false;
    };
    // Event delegation: single listener handles all field events
    // This is like putting one helper on the whole form instead of many helpers on each field
    form.addEventListener('input', (event)=>{
        const field = event.target; // Which field did the user type in?
        const step = field.closest('fieldset[data-step]'); // Which step is this field in?
        const activeStep = steps[currentStepIndex]; // Which step is currently visible?
        // Only validate if field is in current step (don't validate hidden fields)
        if (!step || !activeStep || step !== activeStep) return;
        // For optional fields with maxlength, show live limit feedback
        const isOptional = !field.hasAttribute('required');
        const hasContent = field.value.trim() !== '';
        // 1. Optional field with no content → skip
        if (isOptional && !hasContent) return;
        // Validate on input so custom validators (email/phone) are enforced.
        validateField(field);
    });
    form.addEventListener('change', (event)=>{
        const field = event.target; // Which field did the user change?
        const step = field.closest('fieldset[data-step]'); // Which step is this field in?
        const activeStep = steps[currentStepIndex]; // Which step is currently visible?
        // Only validate if field is in current step and is select/checkbox/radio
        if (!step || !activeStep || step !== activeStep) return;
        if (field instanceof HTMLSelectElement || field instanceof HTMLInputElement && (field.type === 'checkbox' || field.type === 'radio')) validateField(field);
    });
    form.addEventListener('blur', (event)=>{
        const field = event.target; // Which field did the user leave?
        const step = field.closest('fieldset[data-step]'); // Which step is this field in?
        const activeStep = steps[currentStepIndex]; // Which step is currently visible?
        // Only validate if field is in current step
        if (!step || !activeStep || step !== activeStep) return;
        // If Next was just clicked, let step validator handle all fields together
        if (isNavigationHappening) return;
        // Otherwise, validate this individual field
        validateField(field);
    }, true); // Use capture to ensure we get the blur event
    /**
   * Update the current step index for event delegation.
   * Call this when switching between steps.
   *
   * @param {number} stepIndex - The new active step index
   */ const setCurrentStep = (stepIndex)=>{
        currentStepIndex = stepIndex;
    };
    const beginNavigation = ()=>{
        isNavigationHappening = true;
    };
    const endNavigation = ()=>{
        isNavigationHappening = false;
    };
    /**
   * Validate every field inside a given step.
   *
   * 1. Locate the <fieldset> for the requested step index.
   * 2. Collect all interactive fields inside that step (inputs, selects, textareas).
   * 3. Run validateField on each input, remembering the first one that fails.
   * 4. If any field fails and focus=true, move focus to the offending element so
   *    keyboard and screen reader users land exactly where they need to fix data.
   * 5. Return a boolean so callers (Next button, Enter key handler, submit) can
   *    decide whether to continue or halt.
   *
   * @param {number} stepIndex - The index of the step to validate
   * @param {Object} options - Validation options
   * @param {boolean} options.focus - Whether to focus the first invalid field
   * @returns {boolean} True if step is valid, false otherwise
   */ const validateStep = (stepIndex, { focus: focus = true } = {})=>{
        const step = steps[stepIndex];
        if (!step) return true;
        const stepFields = Array.from(step.querySelectorAll('input, select, textarea'));
        let firstInvalidField = null;
        let isStepValid = true;
        for (const field of stepFields){
            const isFieldValid = validateField(field);
            if (!isFieldValid) {
                isStepValid = false;
                if (!firstInvalidField) firstInvalidField = field;
            }
        }
        if (!isStepValid && focus && firstInvalidField instanceof HTMLElement) firstInvalidField.focus();
        return isStepValid;
    };
    return {
        validateStep: /** 
     * Validate all fields in a specific step
     * @type {typeof validateStep}
     */ validateStep,
        setCurrentStep: /** 
     * Update the current active step for event delegation
     * @type {typeof setCurrentStep}
     */ setCurrentStep,
        beginNavigation: beginNavigation,
        endNavigation: endNavigation
    };
}


/**
 * Helper functions for form stepper functionality
 */ /**
 * Reusable modal dialog component using native <dialog> API.
 * Supports success, error, and info types with theme-aware styling.
 */ class $d331e374a3e47758$export$3ddf2d174ce01153 {
    constructor(){
        this.activeDialog = null;
    }
    /**
   * Show a modal dialog.
   * @param {string} title - Dialog title
   * @param {string} message - Dialog message
   * @param {'success'|'error'|'info'} type - Dialog type for styling
   * @param {string} [okText='OK'] - Text for the OK button
   */ show(title, message, type = 'info', okText = 'OK') {
        // Close any existing dialog first
        if (this.activeDialog) this.activeDialog.close();
        const dialog = document.createElement('dialog');
        dialog.className = `dialog dialog--${type}`;
        dialog.setAttribute('aria-labelledby', 'dialog-title');
        dialog.setAttribute('aria-describedby', 'dialog-message');
        // Create icon based on type
        const getIcon = (type)=>{
            switch(type){
                case 'success':
                    return `<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true" class="dialog__icon">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
          </svg>`;
                case 'error':
                    return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512" aria-hidden="true" class="dialog__icon">
            <path fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="32" d="M256 80c-8.66 0-16.58 7.36-16 16l8 216a8 8 0 0 0 8 8h0a8 8 0 0 0 8-8l8-216c.58-8.64-7.34-16-16-16"></path>
            <circle cx="256" cy="416" r="16" fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="32"></circle>
          </svg>`;
                default:
                    return `<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true" class="dialog__icon">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
          </svg>`;
            }
        };
        // Build dialog structure
        const form = document.createElement('form');
        form.method = 'dialog';
        form.className = 'dialog__form';
        const header = document.createElement('header');
        header.className = 'dialog__header';
        const iconContainer = document.createElement('div');
        iconContainer.className = 'dialog__icon-container';
        iconContainer.innerHTML = getIcon(type);
        const titleEl = document.createElement('h2');
        titleEl.id = 'dialog-title';
        titleEl.className = 'dialog__title';
        titleEl.textContent = title;
        header.appendChild(iconContainer);
        header.appendChild(titleEl);
        const main = document.createElement('main');
        main.className = 'dialog__main';
        const messageEl = document.createElement('p');
        messageEl.id = 'dialog-message';
        messageEl.className = 'dialog__message';
        messageEl.textContent = message;
        main.appendChild(messageEl);
        const footer = document.createElement('footer');
        footer.className = 'dialog__footer';
        const menu = document.createElement('menu');
        menu.className = 'dialog__menu';
        const button = document.createElement('button');
        button.type = 'submit';
        button.className = 'btn btn--primary dialog__btn';
        button.textContent = okText;
        menu.appendChild(button);
        footer.appendChild(menu);
        form.appendChild(header);
        form.appendChild(main);
        form.appendChild(footer);
        dialog.appendChild(form);
        // Handle cleanup when dialog closes (from any method)
        dialog.addEventListener('close', ()=>{
            if (dialog.parentNode) dialog.parentNode.removeChild(dialog);
            if (this.activeDialog === dialog) this.activeDialog = null;
        });
        // Append to body and show
        document.body.appendChild(dialog);
        this.activeDialog = dialog;
        dialog.showModal();
        return dialog;
    }
    /**
   * Close the active dialog if one exists
   */ close() {
        if (this.activeDialog) this.activeDialog.close(); // This triggers the 'close' event which handles cleanup
    }
    /**
   * Show a success dialog
   */ success(title, message, okText = 'OK') {
        return this.show(title, message, 'success', okText);
    }
    /**
   * Show an error dialog
   */ error(title, message, okText = 'OK') {
        return this.show(title, message, 'error', okText);
    }
    /**
   * Show an info dialog
   */ info(title, message, okText = 'OK') {
        return this.show(title, message, 'info', okText);
    }
}
const $d331e374a3e47758$export$518824cf31321346 = new $d331e374a3e47758$export$3ddf2d174ce01153();


function $5da9617b38b166c1$export$4f4e67cab29a59e7(step) {
    if (!step) return;
    const candidate = step.querySelector('input, select, textarea, button');
    if (candidate instanceof HTMLElement) candidate.focus();
}
function $5da9617b38b166c1$export$ccfe154c32fb7c8(progressEl) {
    return (activeIndex, totalSteps)=>{
        if (!(progressEl instanceof HTMLElement)) return;
        const pct = (activeIndex + 1) / totalSteps * 100;
        progressEl.style.setProperty('--progress', `${pct}%`);
    };
}
function $5da9617b38b166c1$export$a32e18c93072089f(statusEl) {
    return (activeIndex, totalSteps)=>{
        if (!(statusEl instanceof HTMLElement)) return;
        statusEl.textContent = `Step ${activeIndex + 1} of ${totalSteps}`;
    };
}
function $5da9617b38b166c1$export$6d6b67a3ce8d6906(prevBtn, nextBtn, submitBtn, totalSteps) {
    return (activeIndex)=>{
        // Update prev button
        prevBtn.hidden = activeIndex === 0;
        // Update next button
        nextBtn.hidden = activeIndex === totalSteps - 1;
        // Update submit button
        if (submitBtn) {
            if (activeIndex === totalSteps - 1) {
                submitBtn.removeAttribute('hidden');
                submitBtn.removeAttribute('aria-hidden');
            } else {
                submitBtn.setAttribute('hidden', '');
                submitBtn.setAttribute('aria-hidden', 'true');
            }
        }
    };
}
function $5da9617b38b166c1$export$8f23108d36da8efa(form, submitBtn, setActiveIndex) {
    let failedAttempts = 0;
    const MAX_ATTEMPTS = 3;
    // Reusable function to reset button state
    const resetSubmitButton = ()=>{
        setTimeout(()=>{
            submitBtn.disabled = false;
            submitBtn.textContent = 'Submit Application';
        }, 2000);
    };
    return async (event, validator, steps)=>{
        event.preventDefault();
        // Validate all steps before submit (prevents skipping required fields)
        for(let stepIndex = 0; stepIndex < steps.length; stepIndex += 1){
            const stepValid = validator.validateStep(stepIndex, {
                focus: false
            });
            if (!stepValid) {
                if (typeof setActiveIndex === 'function') setActiveIndex(stepIndex, {
                    focus: false
                });
                return;
            }
        }
        try {
            const formData = new FormData(form);
            const data = Object.fromEntries(formData);
            // Update button state
            submitBtn.disabled = true;
            submitBtn.textContent = 'Submitting...';
            const response = await fetch(`/api/send-email`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(data)
            });
            const responseText = await response.text();
            let result = null;
            try {
                result = responseText ? JSON.parse(responseText) : null;
            } catch  {
                result = null;
            }
            if (!response.ok) {
                failedAttempts++;
                const baseError = result?.error || responseText || `Status ${response.status}`;
                const errorMessage = failedAttempts >= MAX_ATTEMPTS ? `\u{26A0}\u{FE0F} Attempt ${failedAttempts}/${MAX_ATTEMPTS}\n\n${baseError}\n\nPlease contact info@saludclinical.com for help` : `\u{26A0}\u{FE0F} Attempt ${failedAttempts}/${MAX_ATTEMPTS}\n\n${baseError}`;
                (0, $d331e374a3e47758$export$518824cf31321346).error('Submission Failed', errorMessage);
                submitBtn.textContent = "Submission Failed \u274C";
                resetSubmitButton();
                return;
            }
            if (result && result.success) {
                (0, $d331e374a3e47758$export$518824cf31321346).success('Application Sent', "Application sent! We\u2019ll follow up soon.");
                // Show success state briefly before reset
                submitBtn.textContent = "Application Sent! \u2705";
                setTimeout(()=>{
                    form.reset();
                    if (typeof setActiveIndex === 'function') setActiveIndex(0, {
                        focus: false
                    });
                    submitBtn.disabled = false;
                    submitBtn.textContent = 'Submit Application';
                }, 2000);
            } else {
                failedAttempts++;
                const baseError = result && result.error || 'Unknown error';
                const errorMessage = failedAttempts >= MAX_ATTEMPTS ? `\u{26A0}\u{FE0F} Attempt ${failedAttempts}/${MAX_ATTEMPTS}\n\n${baseError}\n\nPlease contact support@example.com for help` : `\u{26A0}\u{FE0F} Attempt ${failedAttempts}/${MAX_ATTEMPTS}\n\n${baseError}`;
                (0, $d331e374a3e47758$export$518824cf31321346).error('Submission Failed', errorMessage);
                submitBtn.textContent = "Submission Failed \u274C";
                resetSubmitButton();
            }
            return;
        } catch (error) {
            console.error("\u274C Network error:", error);
            failedAttempts++;
            const errorMessage = failedAttempts >= MAX_ATTEMPTS ? `\u{26A0}\u{FE0F} Attempt ${failedAttempts}/${MAX_ATTEMPTS}\n\nNetwork error. Please check your connection.\n\nPlease contact support@example.com for help` : `\u{26A0}\u{FE0F} Attempt ${failedAttempts}/${MAX_ATTEMPTS}\n\nNetwork error. Please try again.`;
            (0, $d331e374a3e47758$export$518824cf31321346).error('Network Error', errorMessage);
            submitBtn.textContent = "Network Error \u274C";
            resetSubmitButton();
        }
    };
}


function $3b6e56f0fec48655$export$c2a2a22ed1747525() {
    const forms = Array.from(document.querySelectorAll('form[data-component="ApplyFormCard"]'));
    forms.forEach((form)=>{
        const steps = Array.from(form.querySelectorAll('.apply-step'));
        if (!steps.length) return;
        const prevBtn = form.querySelector('[data-stepper-prev]');
        const nextBtn = form.querySelector('[data-stepper-next]');
        const progress = form.querySelector('[data-stepper-progress]');
        const status = form.querySelector('[data-stepper-status]');
        const submitBtn = form.querySelector('button.apply-submit');
        if (!(prevBtn instanceof HTMLButtonElement) || !(nextBtn instanceof HTMLButtonElement)) return;
        let activeIndex = 0;
        const validator = (0, $251048abdb9fb148$export$c881797088684461)(form);
        // Create helper functions
        const updateProgress = (0, $5da9617b38b166c1$export$ccfe154c32fb7c8)(progress);
        const updateStatus = (0, $5da9617b38b166c1$export$a32e18c93072089f)(status);
        const updateButtons = (0, $5da9617b38b166c1$export$6d6b67a3ce8d6906)(prevBtn, nextBtn, submitBtn, steps.length);
        const setActiveIndex = (nextIndex, { focus: focus = true } = {})=>{
            activeIndex = Math.max(0, Math.min(nextIndex, steps.length - 1));
            // Update validator's current step for event delegation
            validator.setCurrentStep(activeIndex);
            steps.forEach((step, idx)=>{
                const isActive = idx === activeIndex;
                step.toggleAttribute('hidden', !isActive);
            });
            // Update UI components
            updateButtons(activeIndex);
            updateProgress(activeIndex, steps.length);
            updateStatus(activeIndex, steps.length);
            if (focus) (0, $5da9617b38b166c1$export$4f4e67cab29a59e7)(steps[activeIndex]);
        };
        const handleSubmit = (0, $5da9617b38b166c1$export$8f23108d36da8efa)(form, submitBtn, setActiveIndex);
        form.classList.add('is-stepper');
        prevBtn.addEventListener('click', ()=>setActiveIndex(activeIndex - 1));
        nextBtn.addEventListener('pointerdown', ()=>{
            validator.beginNavigation();
        });
        nextBtn.addEventListener('pointerup', ()=>{
            validator.endNavigation();
        });
        nextBtn.addEventListener('click', ()=>{
            validator.beginNavigation();
            const stepValid = validator.validateStep(activeIndex, {
                focus: false
            });
            if (!stepValid) {
                setTimeout(()=>validator.endNavigation(), 0);
                return;
            }
            setActiveIndex(activeIndex + 1);
            // End the navigation suppression after focus/blur settles.
            setTimeout(()=>validator.endNavigation(), 0);
        });
        form.addEventListener('keydown', (e)=>{
            if (e.key !== 'Enter') return;
            if (e.shiftKey) return;
            const target = e.target;
            if (!(target instanceof HTMLElement)) return;
            const step = target.closest('fieldset[data-step]');
            const stepIndex = step ? Array.from(steps).indexOf(step) : -1;
            if (stepIndex === activeIndex) {
                e.preventDefault();
                validator.beginNavigation();
                const stepValid = validator.validateStep(activeIndex, {
                    focus: false
                });
                if (!stepValid) {
                    setTimeout(()=>validator.endNavigation(), 0);
                    return;
                }
                setActiveIndex(activeIndex + 1);
                // End the navigation suppression after focus/blur settles.
                setTimeout(()=>validator.endNavigation(), 0);
            }
        });
        if (submitBtn instanceof HTMLButtonElement) {
            submitBtn.addEventListener('pointerdown', ()=>{
                validator.beginNavigation();
            });
            submitBtn.addEventListener('pointerup', ()=>{
                validator.endNavigation();
            });
        }
        form.addEventListener('submit', (event)=>{
            validator.beginNavigation();
            Promise.resolve(handleSubmit(event, validator, steps)).finally(()=>{
                setTimeout(()=>validator.endNavigation(), 0);
            });
        });
        // Do not auto-focus on initial load; it can scroll the page to the contact section.
        setActiveIndex(0, {
            focus: false
        });
    });
}


// ==================== CONTENT REVEAL SYSTEM ====================
// Customer requested content hiding until site meets standards
// Mobile: Touch screen 5 times
// Desktop: Ctrl + Alt + . (period) key combination
function $01daf4fafa41c667$var$setContentVisibility(isRevealed) {
    localStorage.setItem('contentRevealed', isRevealed.toString());
    document.body.style.display = isRevealed ? 'block' : 'none';
}
function $01daf4fafa41c667$export$c73a0dcee3d332() {
    // Check if this is a page refresh (not first load)
    const navigationEntries = performance.getEntriesByType('navigation');
    const isRefresh = navigationEntries.length > 0 && navigationEntries[0].type === 'reload';
    // Reset to false on page refresh
    if (isRefresh) localStorage.setItem('contentRevealed', 'false');
    // Initialize localStorage to false if not set (first visit)
    if (localStorage.getItem('contentRevealed') === null) localStorage.setItem('contentRevealed', 'false');
    // Set initial visibility based on localStorage
    const isRevealed = localStorage.getItem('contentRevealed') === 'true';
    $01daf4fafa41c667$var$setContentVisibility(isRevealed);
    function revealContent() {
        // Toggle the current state
        const currentState = localStorage.getItem('contentRevealed') === 'true';
        $01daf4fafa41c667$var$setContentVisibility(!currentState);
    }
    // Mobile: Touch screen 5 times to toggle content
    let touchCount = 0;
    let touchTimeout;
    document.addEventListener('touchend', (e)=>{
        // Only count quick taps (not drags/scrolls)
        const touch = e.changedTouches[0];
        const touchDuration = Date.now() - (touch.startTime || Date.now());
        // Only count if it was a quick tap (less than 200ms)
        if (touchDuration < 200) {
            touchCount++;
            clearTimeout(touchTimeout);
            touchTimeout = setTimeout(()=>{
                touchCount = 0;
            }, 2000); // Reset after 2 seconds of no touches
            if (touchCount === 5) {
                revealContent();
                touchCount = 0;
                clearTimeout(touchTimeout);
            }
        }
    });
    // Desktop: Ctrl + Alt + . (period) key combination
    document.addEventListener('keydown', (e)=>{
        if (e.ctrlKey && e.altKey && e.key === '.') {
            e.preventDefault();
            revealContent();
        }
    });
}


/**
 * Client-Side Router for Clean URLs
 * Handles navigation without page reloads
 */ function $baf70a296327720f$export$cf7ee87ede64229c() {
    const router = {
        init () {
            // Handle initial page load
            this.handleRoute();
            // Handle navigation clicks
            document.addEventListener('click', (e)=>{
                const link = e.target.closest('a[href^="/"]');
                if (link) {
                    e.preventDefault();
                    const path = link.getAttribute('href');
                    this.navigateTo(path);
                }
            });
            // Handle browser back/forward buttons
            window.addEventListener('popstate', ()=>{
                this.handleRoute();
            });
            // Handle hash fragment changes (for QR codes and legacy links)
            window.addEventListener('hashchange', ()=>{
                this.handleRoute();
            });
        },
        navigateTo (path) {
            // Update URL without page reload
            window.history.pushState({}, '', path);
            this.handleRoute();
        },
        handleRoute () {
            // Handle both clean URLs (/connect) and hash fragments (#connect)
            const path = window.location.pathname;
            const hash = window.location.hash.replace('#', '');
            // Priority: hash fragment > clean URL
            const target = hash || path;
            const sectionId = this.getPathToSectionId(target);
            const section = document.getElementById(sectionId);
            if (section) {
                // Simple scrollIntoView - let CSS handle the offset
                section.scrollIntoView({
                    behavior: 'smooth',
                    block: 'start'
                });
                // Update navigation active state
                this.updateNavActive(sectionId);
                // If hash fragment was used, update URL to clean URL
                if (hash && path === '/') window.history.replaceState({}, '', `/${sectionId}`);
            }
        },
        getPathToSectionId (path) {
            // Convert clean URLs or hash fragments to section IDs
            // Handle both "/connect" and "connect" (from hash fragments)
            const cleanPath = path.replace('/', '');
            if (cleanPath === '' || cleanPath === 'home') return 'home';
            if (cleanPath === 'locums') return 'locums';
            if (cleanPath === 'about') return 'about';
            if (cleanPath === 'contact') return 'contact';
            if (cleanPath === 'connect') return 'connect';
            // Backward compatibility for old section IDs in QR codes
            if (cleanPath === 'services') return 'locums';
            if (cleanPath === 'about-us') return 'about';
            if (cleanPath === 'apply') return 'connect';
            return 'home'; // fallback
        },
        updateNavActive (sectionId) {
            // Update desktop navigation
            const desktopLinks = document.querySelectorAll('.nav-link');
            desktopLinks.forEach((link)=>{
                const href = link.getAttribute('href');
                const linkSectionId = this.getPathToSectionId(href);
                if (linkSectionId === sectionId) link.classList.add('nav-link--active');
                else link.classList.remove('nav-link--active');
            });
            // Update mobile navigation
            const mobileLinks = document.querySelectorAll('.mobile-nav__link');
            mobileLinks.forEach((link)=>{
                const href = link.getAttribute('href');
                const linkSectionId = this.getPathToSectionId(href);
                if (linkSectionId === sectionId) link.setAttribute('aria-current', 'page');
                else link.removeAttribute('aria-current');
            });
        }
    };
    router.init();
    return router;
}


// import { initFooterYear } from './footer-year.js';
// ==================== MAIN INITIALIZATION ====================
window.addEventListener('load', ()=>{
    requestAnimationFrame(()=>{
        requestAnimationFrame(()=>{
            (0, $baf70a296327720f$export$cf7ee87ede64229c)();
        });
    });
});
document.addEventListener('DOMContentLoaded', ()=>{
    // initContentReveal(); // Initialize content reveal system first
    // initFooterYear(); // Initialize footer year
    (0, $caa975bf6fc5e650$export$7578aff938ece992)();
    (0, $67c73874256ba79d$export$ac308a179e459484)();
    (0, $e1400db3377e1890$export$b1da90ca756b7b6e)();
    (0, $ef70692d977fa26c$export$3bd541efa9ddf5f5)();
    (0, $02511fd2859c1031$export$fa4f072fc2311a31)();
    (0, $4f024ee0b63cbec0$export$f847871b15e871e2)();
    (0, $cd8e2242aac5ca2f$export$c4b2f00439ee46b0)();
    (0, $3b6e56f0fec48655$export$c2a2a22ed1747525)();
});


//# sourceMappingURL=path0.696bc52a.js.map
